import React from "react";

import type {EffectiveTransform} from "contexts/bgTransform";
import type {PreviewTemplate, TilePreviewLayout} from "./tilePreviewGeometry";

export type TilePreview = {
  /** Small WebP of a per-banner image, when it differs from the catalog. */
  thumbnailUrl?: string;
  /** The background's crop on `template`; null means natural size, top-left. */
  transform: EffectiveTransform | null;
  /** Natural size of the full image the transform is in. */
  image: {width: number; height: number};
  template: PreviewTemplate;
  layout: TilePreviewLayout;
  /** The banner's base colour, which shows where the image does not cover it. */
  baseColor: string;
};

type Props = TilePreview & {
  /** The thumbnail; it is stretched to the full image size. */
  src: string;
};

/**
 * A small copy of the banner background: the same two nested CSS transforms
 * that BiggerPicture applies on the canvas, in banner pixels, then scaled down
 * to the tile. The thumbnail has the proportions of the full image, so drawn
 * at the full image size it takes the place of the full image.
 */
export function BgTilePreview({src, transform, image, template, layout, baseColor}: Props) {
  const x = transform?.x ?? 0;
  const y = transform?.y ?? 0;
  const scale = transform?.scale ?? 1;
  const rotate = transform?.rotate ?? 0;
  const sx = transform?.flip.x ? -1 : 1;
  const sy = transform?.flip.y ? -1 : 1;

  return (
    // The base colour also fills the tile around a banner of another shape
    // (a layout's own Instagram Stories image in a horizontal tile).
    <div className="bg-tile-preview" aria-hidden="true" style={{backgroundColor: baseColor}}>
      <div
        className="bg-tile-preview__banner"
        data-preview-template-id={template.id}
        style={{
          width: template.width,
          height: template.height,
          left: layout.left,
          top: layout.top,
          transform: `scale(${layout.scale})`,
          backgroundColor: baseColor,
        }}
      >
        <div
          className="bg-tile-preview__mover"
          style={{transform: `translate(${x}px, ${y}px) scale(${scale}, ${scale})`}}
        >
          <img
            src={src}
            alt=""
            draggable={false}
            style={{
              width: image.width,
              height: image.height,
              transform: `rotate(${rotate}deg) scale(${sx}, ${sy})`,
            }}
          />
        </div>
      </div>
    </div>
  );
}
